import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Chip, Input } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { FormInput, FormTextarea, FormCategorySelector, FormStatusSelector } from '../../base';
import { productFormSchema, ProductFormData } from './schemas';

interface ProductFormProps {
  onSubmit: (data: ProductFormData) => Promise<void>;
  defaultValues?: Partial<ProductFormData>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  isLoadingProduct?: boolean;
}

const ProductForm: React.FC<ProductFormProps> = ({
  onSubmit,
  defaultValues,
  onCancel,
  isSubmitting = false,
  isLoadingProduct = false,
}) => {
  const { t } = useTranslation('admin');

  const { control, handleSubmit, watch, reset } = useForm({
    resolver: zodResolver(productFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      tagline: '',
      description: '',
      categoryId: '',
      status: 'operational',
      icon: '',
      url: '',
      features: [],
      targetUsers: [],
      slackChannels: [],
      contactPerson: undefined,
      metrics: {},
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, isLoadingProduct, reset]);

  const {
    fields: featureFields,
    append: appendFeature,
    remove: removeFeature,
  } = useFieldArray({
    control: control as any,
    name: 'features',
  });

  const {
    fields: targetUserFields,
    append: appendTargetUser,
    remove: removeTargetUser,
  } = useFieldArray({
    control: control as any,
    name: 'targetUsers',
  });

  const {
    fields: slackChannelFields,
    append: appendSlackChannel,
    remove: removeSlackChannel,
  } = useFieldArray({
    control: control as any,
    name: 'slackChannels',
  });

  const [newFeature, setNewFeature] = useState('');
  const [newUser, setNewUser] = useState('');
  const [newSlackChannel, setNewSlackChannel] = useState('');

  const handleFormSubmit: SubmitHandler<ProductFormData> = (data) => {
    onSubmit(data as ProductFormData);
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      appendFeature(newFeature.trim());
      setNewFeature('');
    }
  };

  const handleAddUser = () => {
    if (newUser.trim()) {
      appendTargetUser(newUser.trim());
      setNewUser('');
    }
  };

  const handleAddSlackChannel = () => {
    if (newSlackChannel.trim()) {
      appendSlackChannel(newSlackChannel.trim());
      setNewSlackChannel('');
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormInput
        name="name"
        control={control}
        label={t('products.modal.form.name.label')}
        placeholder={t('products.modal.form.name.placeholder')}
        isRequired
        variant="bordered"
        isDisabled={isSubmitting || isLoadingProduct}
      />

      <FormInput
        name="tagline"
        control={control}
        label={t('products.modal.form.tagline.label')}
        placeholder={t('products.modal.form.tagline.placeholder')}
        variant="bordered"
        isDisabled={isSubmitting || isLoadingProduct}
      />

      <FormTextarea
        name="description"
        control={control}
        label={t('products.modal.form.description.label')}
        placeholder={t('products.modal.form.description.placeholder')}
        variant="bordered"
        minRows={3}
        isDisabled={isSubmitting || isLoadingProduct}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormCategorySelector
          name="categoryId"
          control={control}
          isDisabled={isSubmitting || isLoadingProduct}
          label={t('products.modal.form.category.label')}
        />

        <FormStatusSelector
          name="status"
          control={control}
          label={t('products.modal.form.status.label')}
          variant="bordered"
          isDisabled={isSubmitting || isLoadingProduct}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormInput
          name="icon"
          control={control}
          label={t('products.modal.form.icon.label')}
          placeholder={t('products.modal.form.icon.placeholder')}
          variant="bordered"
          isDisabled={isSubmitting || isLoadingProduct}
        />
        <FormInput
          name="url"
          control={control}
          label={t('products.modal.form.url.label')}
          placeholder={t('products.modal.form.url.placeholder')}
          type="url"
          variant="bordered"
          isDisabled={isSubmitting || isLoadingProduct}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('products.modal.form.features.label')}</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {featureFields.map((field, index) => (
            <Chip key={field.id} onClose={() => removeFeature(index)} variant="flat" color="primary">
              {watch(`features.${index}`)}
            </Chip>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t('products.modal.form.features.placeholder')}
            value={newFeature}
            onValueChange={setNewFeature}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddFeature();
              }
            }}
            isDisabled={isSubmitting || isLoadingProduct}
            className="flex-1"
            variant="bordered"
          />
          <Button type="button" onPress={handleAddFeature} isIconOnly isDisabled={isSubmitting || isLoadingProduct}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('products.modal.form.targetUsers.label')}</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {targetUserFields.map((field, index) => (
            <Chip key={field.id} onClose={() => removeTargetUser(index)} variant="flat" color="secondary">
              {watch(`targetUsers.${index}`)}
            </Chip>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t('products.modal.form.targetUsers.placeholder')}
            value={newUser}
            onValueChange={setNewUser}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddUser();
              }
            }}
            isDisabled={isSubmitting || isLoadingProduct}
            className="flex-1"
            variant="bordered"
          />
          <Button type="button" onPress={handleAddUser} isIconOnly isDisabled={isSubmitting || isLoadingProduct}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('products.modal.form.slackChannels.label')}</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {slackChannelFields.map((field, index) => (
            <Chip key={field.id} onClose={() => removeSlackChannel(index)} variant="flat" color="warning">
              {watch(`slackChannels.${index}`)}
            </Chip>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t('products.modal.form.slackChannels.placeholder')}
            value={newSlackChannel}
            onValueChange={setNewSlackChannel}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddSlackChannel();
              }
            }}
            isDisabled={isSubmitting || isLoadingProduct}
            className="flex-1"
            variant="bordered"
          />
          <Button
            type="button"
            onPress={handleAddSlackChannel}
            isIconOnly
            isDisabled={isSubmitting || isLoadingProduct}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('products.modal.form.contactPerson.label')}</label>
        <div className="grid grid-cols-3 gap-2">
          <FormInput
            name={'contactPerson.name' as any}
            control={control}
            placeholder={t('products.modal.form.contactPerson.name')}
            variant="bordered"
            isDisabled={isSubmitting || isLoadingProduct}
          />
          <FormInput
            name={'contactPerson.email' as any}
            control={control}
            placeholder={t('products.modal.form.contactPerson.email')}
            type="email"
            variant="bordered"
            isDisabled={isSubmitting || isLoadingProduct}
          />
          <FormInput
            name={'contactPerson.role' as any}
            control={control}
            placeholder={t('products.modal.form.contactPerson.role')}
            variant="bordered"
            isDisabled={isSubmitting || isLoadingProduct}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4">
        {onCancel && (
          <Button variant="light" onPress={onCancel} isDisabled={isSubmitting || isLoadingProduct}>
            {t('common.cancel')}
          </Button>
        )}
        <Button color="primary" type="submit" isLoading={isSubmitting || isLoadingProduct}>
          {defaultValues?.name ? t('common.update') : t('common.create')}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
